import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTranslation } from '@tyro/i18n';
import { type ReactNode, useId, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  type DialogProps,
  DialogTitle,
} from '../dialog';

export interface ConfirmDialogProps {
  title: string;
  description?: string;
  content?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  open: boolean;
  onConfirm: () => void;
  onClose: () => void;
  isDelete?: boolean;
  slots?: {
    leftActionsAdornment?: ReactNode;
  };
  slotProps?: {
    dialog?: Partial<DialogProps>;
  };
}

export function ConfirmDialog({
  title,
  description,
  content,
  confirmText,
  cancelText,
  open,
  onConfirm,
  onClose,
  isDelete = false,
  slots,
  slotProps,
}: ConfirmDialogProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation(['common']);

  if (!content && !description) {
    throw new Error(
      'You must provide either a description or content to the ConfirmDialog component.',
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      {...slotProps?.dialog}
    >
      <DialogTitle onClose={onClose} id={titleId}>
        {title}
      </DialogTitle>
      <DialogContent>
        {content ?? (
          <DialogContentText id={descriptionId}>
            {description}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions
        sx={{ flexDirection: 'row-reverse', justifyContent: 'space-between' }}
      >
        <Stack direction="row" spacing={1.5}>
          <Button autoFocus color="inherit" variant="soft" onClick={onClose}>
            {cancelText || t('common:actions.cancel')}
          </Button>
          <Button
            variant="contained"
            color={isDelete ? 'error' : 'primary'}
            loading={loading}
            onClick={async () => {
              setLoading(true);
              try {
                await (async () => {
                  await onConfirm();
                })();
                onClose();
              } finally {
                setLoading(false);
              }
            }}
          >
            {confirmText || t('common:actions.confirm')}
          </Button>
        </Stack>
        {slots?.leftActionsAdornment}
      </DialogActions>
    </Dialog>
  );
}
